import React, { useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { OBJECT_TYPE, WORKFLOW_STEP_MANUAL_STATUS } from "common/constants";
import { buildLink } from "common/routing";
import { GetWorkflowQuery } from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { Button } from "../Common/Buttons/Button";

type WorkflowStep = Omit<
  NonNullable<GetWorkflowQuery["workflow"]>["workflowSteps"][number],
  "status"
>;

const isManualStatusEnabledForPublicUser = (
  manualStatus: Maybe<WORKFLOW_STEP_MANUAL_STATUS>,
): boolean => {
  if (!manualStatus) {
    return true;
  }

  switch (manualStatus) {
    case WORKFLOW_STEP_MANUAL_STATUS.NOT_STARTED:
    case WORKFLOW_STEP_MANUAL_STATUS.LOCKED:
    case WORKFLOW_STEP_MANUAL_STATUS.NOT_APPLICABLE:
      return false;
    case WORKFLOW_STEP_MANUAL_STATUS.IN_PROGRESS:
    case WORKFLOW_STEP_MANUAL_STATUS.OPEN:
    case WORKFLOW_STEP_MANUAL_STATUS.COMPLETE:
    case WORKFLOW_STEP_MANUAL_STATUS.REJECTED:
      return true;
  }
};

const shouldForceViewOnly = (
  manualStatus: Maybe<WORKFLOW_STEP_MANUAL_STATUS>,
): boolean => {
  if (!manualStatus) {
    return false;
  }

  return (
    manualStatus === WORKFLOW_STEP_MANUAL_STATUS.COMPLETE ||
    manualStatus === WORKFLOW_STEP_MANUAL_STATUS.REJECTED
  );
};

const getCtaText = (workflowStep: WorkflowStep): string => {
  return workflowStep.submission ? "View" : "Create";
};

const getOnCallToActionClick = ({
  canCreateSubmission,
  canViewSubmission,
  workflowStep,
  push,
  pathname,
  objectId,
  objectType,
  isManualMode,
  isPublic,
}: {
  canCreateSubmission: boolean;
  canViewSubmission: boolean;
  workflowStep: WorkflowStep;
  push: ReturnType<typeof useHistory>["push"];
  pathname: string;
  objectId?: string;
  objectType?: OBJECT_TYPE;
  isManualMode: boolean;
  isPublic: boolean;
}): (() => void) | undefined => {
  // In manual mode for public users, check manual status
  if (
    isManualMode &&
    isPublic &&
    !isManualStatusEnabledForPublicUser(workflowStep.manualStatus)
  ) {
    return undefined;
  }

  // Force view-only for complete/rejected in manual mode
  const forceViewOnly =
    isManualMode && isPublic && shouldForceViewOnly(workflowStep.manualStatus);

  if (canCreateSubmission && !workflowStep.submission && !forceViewOnly) {
    return () =>
      push(
        buildLink(
          "createSubmission",
          {
            submissionTypeId:
              workflowStep.workflowStepDefinition.submissionType.id,
            objectId: objectId ?? undefined,
          },
          {
            workflowStepId: workflowStep.id,
            objectType: objectType ?? undefined,
          },
        ),
        { prevLocation: pathname },
      );
  }

  if (workflowStep.submission && canViewSubmission) {
    return () =>
      push({
        pathname: buildLink("editSubmission", {
          submissionId: workflowStep.submission!.id,
        }),
        state: { prevLocation: pathname },
      });
  }

  return undefined;
};

export const WorkflowStepCTA = ({
  workflowStep,
  objectId,
  objectType,
  isManualMode,
  canCreate,
  canView,
}: {
  workflowStep: WorkflowStep;
  objectId?: string;
  objectType?: OBJECT_TYPE;
  isManualMode: boolean;
  canCreate: boolean;
  canView: boolean;
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { isPublic } = useContext(AuthContext);

  const onCallToActionClick = getOnCallToActionClick({
    canCreateSubmission: canCreate,
    canViewSubmission: canView,
    workflowStep,
    push: history.push,
    pathname,
    objectId,
    objectType,
    isManualMode,
    isPublic,
  });

  const ctaText = getCtaText(workflowStep);

  //we are hiding the button for public users when they can't take action on the step
  if (!onCallToActionClick && isPublic) {
    return null;
  }

  //whereas we show the disabled button to a view_only internal user if it's "create"
  return (
    <Button
      variant="outline"
      size="small"
      onPress={onCallToActionClick}
      isDisabled={!onCallToActionClick}
      data-testid="workflow-step-cta"
      title={ctaText}
    />
  );
};
